import React from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

function parseOcrData(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function fmtDob(val) {
  if (!val) return '—'
  const s = String(val).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (dmy) return `${dmy[1].padStart(2, '0')}/${dmy[2].padStart(2, '0')}/${dmy[3]}`
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) {
    const dd = String(parsed.getUTCDate()).padStart(2, '0')
    const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = parsed.getUTCFullYear()
    return `${dd}/${mm}/${yyyy}`
  }
  return s.slice(0, 32)
}

function cardNumber(ocr) {
  if (!ocr) return '—'
  return (
    ocr.document_card_number ||
    ocr.licence_card_number ||
    ocr.rta_number ||
    '—'
  )
}

function docTypeLabel(type) {
  if (!type) return 'ID'
  const t = String(type).toLowerCase()
  if (t.includes('licence') || t.includes('license')) return 'DL'
  if (t.includes('passport')) return 'Passport'
  return type.replace(/_/g, ' ')
}

function hasOcrContent(ocr) {
  if (!ocr || typeof ocr !== 'object') return false
  return [
    ocr.given_name,
    ocr.family_name,
    ocr.document_number,
    ocr.document_card_number,
    ocr.expiry_date,
    ocr.address_line,
  ].some((v) => v != null && String(v).trim() !== '')
}

/**
 * Read-only panel for structured licence/OCR extract retained after image purge.
 * @param {{ compact?: boolean, collapsible?: boolean, expanded?: boolean, onToggle?: () => void }} props
 */
export default function OcrExtractPanel({
  ocrData,
  title = 'ID extract (retained after image purge)',
  subtitle,
  compact = false,
  collapsible = false,
  expanded = true,
  onToggle,
  columns = 1,
}) {
  const ocr = parseOcrData(ocrData)
  if (!hasOcrContent(ocr)) return null

  if (compact) {
    const name = [ocr.given_name, ocr.family_name].filter(Boolean).join(' ')
    const card = cardNumber(ocr)
    const address = [ocr.address_line, ocr.address_suburb, ocr.address_state, ocr.address_postcode]
      .filter((v) => v != null && String(v).trim() !== '')
      .join(', ')
    const rows = [
      ['Type', docTypeLabel(ocr.document_type)],
      ['Document no.', ocr.document_number],
      ['State', ocr.state_of_issue],
      ['Name', name || null],
      ['DOB', ocr.date_of_birth ? fmtDob(ocr.date_of_birth) : null],
      ['Expiry', ocr.expiry_date ? fmtDob(ocr.expiry_date) : null],
      ['Card ID', card !== '—' ? card : null],
      ['Address', address || null],
    ].filter(([, value]) => value != null && String(value).trim() !== '')
    const summaryBits = [
      ocr.document_number || null,
      ocr.state_of_issue || null,
      name || null,
    ].filter(Boolean)
    const showBody = !collapsible || expanded

    return (
      <div className="rounded border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-xs text-gray-800">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-start gap-1.5 text-left"
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDownIcon className="w-4 h-4 text-indigo-700 shrink-0 mt-0.5" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-indigo-700 shrink-0 mt-0.5" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-indigo-900">{title}</div>
              {!expanded && summaryBits.length > 0 ? (
                <div className="text-[10px] text-indigo-800/80 truncate mt-0.5">
                  {summaryBits.join(' · ')}
                </div>
              ) : null}
            </div>
          </button>
        ) : (
          <div className="text-sm font-semibold text-indigo-900 mb-1.5">{title}</div>
        )}
        {showBody ? (
          <>
            <dl
              className={
                columns >= 2
                  ? `grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0 ${collapsible ? 'mt-1.5' : ''}`
                  : `divide-y divide-indigo-100/80 ${collapsible ? 'mt-1.5' : ''}`
              }
            >
              {rows.map(([label, value]) => (
                <div
                  key={label}
                  className={
                    columns >= 2
                      ? 'flex gap-2 py-1 items-start border-b border-indigo-100/60 last:border-0'
                      : 'flex gap-3 py-2 items-start'
                  }
                >
                  <dt className="w-24 shrink-0 text-[10px] font-semibold text-gray-500 pt-0.5 sm:text-xs">
                    {label}
                  </dt>
                  <dd className="min-w-0 flex-1 font-medium text-gray-900 break-words text-xs">{value}</dd>
                </div>
              ))}
            </dl>
            {subtitle ? (
              <div className="text-[10px] text-indigo-700/75 break-words mt-1.5 pt-1 border-t border-indigo-100/80">
                {subtitle}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    )
  }

  const rows = [
    ['Document type', ocr.document_type || '—'],
    ['Licence / document no.', ocr.document_number || '—'],
    ['Card ID (RTA)', cardNumber(ocr)],
    ['Given name', ocr.given_name || '—'],
    ['Family name', ocr.family_name || '—'],
    ['Date of birth', fmtDob(ocr.date_of_birth)],
    ['Expiry', fmtDob(ocr.expiry_date)],
    ['State of issue', ocr.state_of_issue || '—'],
    ['Address', ocr.address_line || '—'],
    ['Suburb', ocr.address_suburb || '—'],
    ['Postcode', ocr.address_postcode || '—'],
    ['Country', ocr.country_code || '—'],
    ['OCR source', ocr.ocr_source || '—'],
    ['Captured', ocr.captured_at ? new Date(ocr.captured_at).toLocaleString() : '—'],
  ]

  const gridClass =
    columns >= 2
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0 text-sm'
      : 'divide-y divide-indigo-100/80 text-sm'

  return (
    <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-indigo-900 mb-1">{title}</h4>
      <p className="text-[10px] text-indigo-700/80 mb-2">
        {subtitle ||
          'Structured fields from licence OCR — kept for audit when ID images are deleted after DVS.'}
      </p>
      <dl className={gridClass}>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className={
              columns >= 2
                ? 'flex gap-2 py-1 items-start border-b border-indigo-100/60'
                : 'flex gap-3 py-2 items-start'
            }
          >
            <dt className="w-28 shrink-0 text-[11px] font-medium text-gray-500 pt-0.5">{label}</dt>
            <dd className="min-w-0 flex-1 font-medium text-gray-900 break-words text-xs">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
